import { forwardRef, useState } from "react";
import { ControllerRenderProps } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { MonthPicker } from "./ui/monthPicker";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLocale } from "@/hooks/useLocale";
import { DEFAULT_DATE_FORMAT } from "@/constants";

interface MonthSelectProps extends ControllerRenderProps {
  className?: string;
  inDialog?: boolean;
  initialDate?: Date;
  selectableFromDate?: Date;
  selectableToDate?: Date;
}

export default forwardRef<HTMLButtonElement, MonthSelectProps>(
  function MonthSelect(
    {
      value,
      onChange,
      className,
      inDialog,
      initialDate,
      selectableFromDate,
      selectableToDate,
      ...props
    },
    ref,
  ) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const locale = useLocale();
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-full justify-start text-left font-normal capitalize",
              !value && "text-muted-foreground",
              className,
            )}
            {...props}
            ref={ref}
            data-testid="month-select"
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value
              ? format(new Date(value), "LLLL yyyy", { locale })
              : t("monthSelect.placeholder")}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className={cn("w-auto p-0", {
            "pointer-events-auto": inDialog,
          })}
          align="start"
        >
          <MonthPicker
            value={initialDate || (value ? new Date(value) : undefined)}
            onChange={(date) => {
              onChange(format(date, DEFAULT_DATE_FORMAT));
              setOpen(false);
            }}
            selectableFromDate={selectableFromDate}
            selectableToDate={selectableToDate}
          />
        </PopoverContent>
      </Popover>
    );
  },
);
